import React from 'react';

import PropTypes from 'prop-types';
import AvatarImage from 'react-avatar';
import * as cn from 'classnames';
import { IoMdImage } from 'react-icons/io';

import { Indicator, Text, Menu } from 'modules/common';

const RADIUS_OPTS = {
  xs: 'rounded-sm',
  sm: 'rounded',
  default: 'rounded-md',
  lg: 'rounded-lg',
  rounded: 'rounded-full',
};
const dropDownIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <rect x="0.5" y="0.5" width="15" height="15" rx="3.5" fill="#FAFAFA" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.23431 5.83435C4.54673 5.52193 5.05327 5.52193 5.36569 5.83435L8 8.46867L10.6343 5.83435C10.9467 5.52193 11.4533 5.52193 11.7657 5.83435C12.0781 6.14677 12.0781 6.6533 11.7657 6.96572L8.56569 10.1657C8.25327 10.4781 7.74673 10.4781 7.43431 10.1657L4.23431 6.96572C3.9219 6.6533 3.9219 6.14677 4.23431 5.83435Z"
      fill="#AFAFAF"
    />
    <rect x="0.5" y="0.5" width="15" height="15" rx="3.5" stroke="#E4E4E4" />
  </svg>
);

function Avatar({
  className,
  indicatorType,
  indicatorSize,
  radius,
  name,
  textSize,
  src,
  size,
  withIndicator,
  indicatorPositionClassName,
  containerClassName,
  title,
  subTitle,
  detailsContainerClassName,
  withDropdown,
  menuProps,
  defaultImageClassName,
  ...rest
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const ref = React.useRef();

  const style = {
    radius: RADIUS_OPTS?.[radius],
  };

  return (
    <div
      className={cn(
        'flex leading-none gap-ds-8 items-center relative',
        containerClassName
      )}
      ref={ref}
    >
      <div className="relative">
        {!name && !title && !src ? (
          <div
            className={cn(
              'flex items-center justify-center',
              style.radius,
              'bg-disabled'
            )}
            style={{ height: size, width: size }}
          >
            <IoMdImage
              className={cn(
                'h-4 w-4 m-auto rounded-md text-muted',
                defaultImageClassName
              )}
            />
          </div>
        ) : (
          <AvatarImage
            name={name || title}
            alt={name}
            className={cn(className, style.radius, {
              'cursor-pointer':
                withDropdown && document.body.scrollWidth <= 768,
            })}
            textSizeRatio={textSize}
            textMarginRatio={0.25}
            src={src}
            size={size}
            color="#EDEDED"
            fgColor="#606774"
            onClick={() => {
              if (document.body.scrollWidth > 768) return;

              setIsOpen(!isOpen);
            }}
            {...rest}
          />
        )}

        {withIndicator && (
          <Indicator
            positionClassName={indicatorPositionClassName}
            size={indicatorSize}
            type={indicatorType}
            isAbsolute
          />
        )}
      </div>

      {(Boolean(title) || Boolean(subTitle)) && (
        <div className={cn('flex flex-col', detailsContainerClassName)}>
          <Text type="caption" color="muted" weight="bold">
            {title}
          </Text>

          <Text type="caption" color="muted">
            {subTitle}
          </Text>
        </div>
      )}

      {withDropdown && (
        <div className="hidden md:block relative ml-ds-4">
          <button type="button" onClick={() => setIsOpen(!isOpen)}>
            {dropDownIcon}
          </button>
        </div>
      )}

      <Menu
        isOpen={isOpen}
        width="200px"
        className="right-0 mt-px top-9 md:top-[26px]"
        emptyStateProps={{
          description: '',
        }}
        clickOutsideProps={{
          ref,
          set: setIsOpen,
        }}
        {...menuProps}
      />
    </div>
  );
}
Avatar.defaultProps = {
  className: '',
  name: '',
  textSize: 2,
  src: null,
  size: '60px',
  radius: 'default',
  indicatorPositionClassName: 'bottom-0 right-0',
  indicatorType: 'default',
  indicatorSize: 'default',
  withIndicator: false,
  withDropdown: false,
  containerClassName: '',
  title: '',
  subTitle: '',
  detailsContainerClassName: '',
  defaultImageClassName: '',
  menuProps: {},
};

Avatar.propTypes = {
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  name: PropTypes.string,
  detailsContainerClassName: PropTypes.string,
  defaultImageClassName: PropTypes.string,
  menuProps: PropTypes.instanceOf(Object),
  textSize: PropTypes.number,
  withIndicator: PropTypes.bool,
  withDropdown: PropTypes.bool,
  containerClassName: PropTypes.string,
  src: PropTypes.string,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  indicatorType: PropTypes.string,
  indicatorPositionClassName: PropTypes.string,
  indicatorSize: PropTypes.string,
  radius: PropTypes.oneOf(Object.keys(RADIUS_OPTS)),
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default Avatar;
